import { useEffect, useRef, useState } from "react";
import moment from "moment";
import useFetch from "use-http";
import ChartJS, { type ChartConfiguration } from "chart.js/auto";

import Spinner from "app/components/shared/Spinner";
import { DateFilter } from "app/components/analytics/shared/DateFilters";
import { LoadingChart } from "app/components/analytics/shared/LoadingStates";
import { Metrics, ChartData } from "app/components/analytics/shared/type";
import { chartOptions } from "app/components/analytics/shared/chartHelpers";

type ChartMetrics = Pick<Metrics, "chartType" | "metric">;

type Props = ChartMetrics & {
  height: string | number;
  width?: string | number;
  filter: DateFilter;
  branch: string | null | undefined;
  label?: string | null | undefined;
  endpoint: string;
  options?: object | null | undefined;
  tagString?: string | null;
  groupByTagString?: string | null;
};

type ChartType = { destroy: () => void };

const Chart = (props: Props) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  let chart: ChartType;

  ChartJS.defaults.font.family =
    "-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Helvetica, sans-serif";

  const [chartData, setChartData] = useState<ChartData | null>(null);
  // TODO: Refactor URL construction into the Rails view.
  // Currently, constructing the URL here requires passing every param/filter needed
  // for chart data, leading to unnecessary complexity.
  const url = new URL("chart_data", props.endpoint);
  url.searchParams.append(
    "from",
    moment(props.filter.from).format("YYYY-MM-DD"),
  );
  url.searchParams.append("metric", props.metric);
  if (props.branch != null) {
    url.searchParams.append("branch", props.branch);
  }
  if (props.tagString) {
    url.searchParams.append("tags", props.tagString);
  }
  if (props.groupByTagString) {
    url.searchParams.append("group_by_tag", props.groupByTagString);
  }

  const { get, response, loading, error } = useFetch(url.href);

  useEffect(() => {
    loadChartData();
  }, [props.filter, props.branch, props.tagString]);

  const loadChartData = async () => {
    const data = await get();
    if (response.ok) {
      setChartData(data);
    }
  };

  useEffect(() => {
    if (chartData && canvasRef.current) {
      const options = chartOptions(
        props.chartType,
        chartData,
        props.metric,
        props.label,
        props.options,
      ) as ChartConfiguration;

      chart = new ChartJS(canvasRef.current, options);
    }

    return () => {
      if (chart) {
        chart.destroy();
      }
    };
  }, [chartData]);

  if (error) {
    return <div>There was an error loading the chart</div>;
  }

  return (
    <div data-testid="chart-container" style={{ height: props.height }}>
      {/* use the LoadingChart for MiniChart and Spinner otherwise */}
      {loading && props.height === 50 && <LoadingChart barWidth="25px" />}
      {loading && props.height !== 50 && <Spinner />}
      {!loading && chartData && (
        <canvas
          height={props.height}
          width={props.width}
          ref={canvasRef}
          data-testid="canvas"
        />
      )}
    </div>
  );
};

export default Chart;
